import { TokenManager, authenticatedFetch } from "@/supabase/supabaseClient";
import type { Team, TeamMember } from "@/types/task";
import type { UserProfile } from "@/types/user";

// Extended type for team members with user profile data
interface TeamMemberWithProfile extends TeamMember {
  user_profiles: UserProfile;
}

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

export class TeamService {
  /**
   * Fetch all teams that the current user has access to
   */
  static async getAllTeams(tenantId: string): Promise<Team[]> {
    try {
      // Ensure token is ready before making API call
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot fetch teams");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/teams?select=id,name,description,created_by,created_at,updated_at&tenant_id=eq.${tenantId}&order=name`
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching teams:", error);
        throw new Error(`Failed to fetch teams: ${response.statusText}`);
      }

      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error("Error in getAllTeams:", error);
      throw error;
    }
  }

  /**
   * Fetch teams where the current user is a member
   */
  static async getUserTeams(userId: string, tenantId: string): Promise<Team[]> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot fetch user teams");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/teams?select=id,name,description,created_by,created_at,updated_at,team_members!inner(user_id)&team_members.user_id=eq.${userId}&tenant_id=eq.${tenantId}&order=name`
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching user teams:", error);
        throw new Error(`Failed to fetch user teams: ${response.statusText}`);
      }

      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error("Error in getUserTeams:", error);
      throw error;
    }
  }

  /**
   * Get team by ID
   */
  static async getTeamById(id: string, tenantId: string): Promise<Team | null> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot fetch team by ID");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/teams?select=id,name,description,created_by,created_at,updated_at&id=eq.${id}&tenant_id=eq.${tenantId}`
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching team by ID:", error);
        return null;
      }

      const data = await response.json();
      return data && data.length > 0 ? data[0] : null;
    } catch (error) {
      console.error("Error in getTeamById:", error);
      return null;
    }
  }

  /**
   * Create a new team
   */
  static async createTeam(
    teamData: {
      name: string;
      description?: string;
    },
    tenantId: string
  ): Promise<Team> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot create team");
        throw new Error("Authentication token not available");
      }

      // Get current user from stored token data
      const user = await TokenManager.getStoredUserAsync();
      if (!user) {
        throw new Error("You must be logged in to create a team.");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/teams`,
        {
          method: "POST",
          headers: {
            Prefer: "return=representation",
          },
          body: JSON.stringify({
            name: teamData.name.trim(),
            description: teamData.description?.trim() || null,
            created_by: user.id,
            tenant_id: tenantId,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error creating team:", errorText);

        // Check if it's a duplicate name error
        if (
          response.status === 409 ||
          errorText.includes("teams_name_unique")
        ) {
          throw new Error(
            `A team with the name "${teamData.name}" already exists. Please choose a different name.`
          );
        }
        // Check for RLS policy violation
        if (
          response.status === 403 ||
          errorText.includes("row-level security policy")
        ) {
          throw new Error(
            "You do not have permission to create teams. Please contact an administrator."
          );
        }
        throw new Error(`Failed to create team: ${response.statusText}`);
      }

      const data = await response.json();
      return data && data.length > 0 ? data[0] : data;
    } catch (error) {
      console.error("Error in createTeam:", error);
      throw error;
    }
  }

  /**
   * Check if a team name already exists
   */
  static async checkTeamNameExists(
    name: string,
    tenantId: string
  ): Promise<boolean> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot check team name");
        return false;
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/teams?select=id&name=eq.${encodeURIComponent(
          name
        )}&tenant_id=eq.${tenantId}&limit=1`
      );

      if (!response.ok) {
        console.error("Error checking team name:", response.statusText);
        return false;
      }

      const data = await response.json();
      return data && data.length > 0;
    } catch (error) {
      console.error("Error in checkTeamNameExists:", error);
      return false;
    }
  }

  /**
   * Update an existing team
   */
  static async updateTeam(
    id: string,
    teamData: { name: string; description?: string },
    tenantId: string
  ): Promise<Team> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot update team");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/teams?id=eq.${id}&tenant_id=eq.${tenantId}`,
        {
          method: "PATCH",
          headers: {
            Prefer: "return=representation",
          },
          body: JSON.stringify({
            name: teamData.name.trim(),
            description: teamData.description?.trim() || null,
            updated_at: new Date().toISOString(),
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error updating team:", errorText);

        // Check if it's a duplicate name error
        if (
          response.status === 409 ||
          errorText.includes("teams_name_unique")
        ) {
          throw new Error(
            `A team with the name "${teamData.name}" already exists. Please choose a different name.`
          );
        }
        // Check if team doesn't exist
        if (response.status === 404) {
          throw new Error(
            "Team not found or you do not have permission to update it."
          );
        }
        throw new Error(`Failed to update team: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data || data.length === 0) {
        throw new Error(
          "Team not found or you do not have permission to update it."
        );
      }

      return data[0];
    } catch (error) {
      console.error("Error in updateTeam:", error);
      throw error;
    }
  }

  /**
   * Delete a team (and its members)
   */
  static async deleteTeam(teamId: string, tenantId: string): Promise<void> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot delete team");
        throw new Error("Authentication token not available");
      }

      // First delete team members
      const membersResponse = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/team_members?team_id=eq.${teamId}&tenant_id=eq.${tenantId}`,
        {
          method: "DELETE",
        }
      );

      if (!membersResponse.ok) {
        console.warn(
          "Warning: Failed to delete team members:",
          membersResponse.statusText
        );
      }

      // Then delete the team
      const teamResponse = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/teams?id=eq.${teamId}&tenant_id=eq.${tenantId}`,
        {
          method: "DELETE",
        }
      );

      if (!teamResponse.ok) {
        const error = await teamResponse.text();
        console.error("Error deleting team:", error);
        throw new Error(`Failed to delete team: ${teamResponse.statusText}`);
      }
    } catch (error) {
      console.error("Error in deleteTeam:", error);
      throw error;
    }
  }

  /**
   * Get team members for a specific team
   */
  static async getTeamMembers(
    teamId: string,
    tenantId: string
  ): Promise<TeamMemberWithProfile[]> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot fetch team members");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/team_members?select=id,team_id,user_id,role,joined_at,user_profiles!team_members_user_id_fkey(id,email,first_name,last_name,user_roles!user_roles_user_id_fkey(role_id,roles(id,name)))&team_id=eq.${teamId}&tenant_id=eq.${tenantId}&order=joined_at`
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching team members:", error);
        throw new Error(`Failed to fetch team members: ${response.statusText}`);
      }

      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error("Error in getTeamMembers:", error);
      throw error;
    }
  }

  /**
   * Get all team members across all teams
   */
  static async getAllTeamMembers(
    tenantId: string
  ): Promise<TeamMemberWithProfile[]> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot fetch all team members");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/team_members?select=id,team_id,user_id,role,joined_at,user_profiles!team_members_user_id_fkey(id,email,first_name,last_name,user_roles!user_roles_user_id_fkey(role_id,roles(id,name)))&tenant_id=eq.${tenantId}&order=joined_at`
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching all team members:", error);
        throw new Error(`Failed to fetch team members: ${response.statusText}`);
      }

      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error("Error in getAllTeamMembers:", error);
      throw error;
    }
  }

  /**
   * Add a user to a team
   */
  static async addTeamMember(
    teamId: string,
    userId: string,
    role: "admin" | "member" = "member",
    tenantId: string
  ): Promise<TeamMemberWithProfile> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot add team member");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/team_members`,
        {
          method: "POST",
          headers: {
            Prefer: "return=representation",
          },
          body: JSON.stringify({
            team_id: teamId,
            user_id: userId,
            role: role,
            tenant_id: tenantId,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error adding team member:", errorText);

        // Check for duplicate membership
        if (response.status === 409) {
          throw new Error("User is already a member of this team.");
        }
        throw new Error(`Failed to add team member: ${response.statusText}`);
      }

      const teamMemberData = await response.json();
      const createdMember =
        teamMemberData && teamMemberData.length > 0
          ? teamMemberData[0]
          : teamMemberData;

      // Fetch the complete member data with user profile
      const memberResponse = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/team_members?select=id,team_id,user_id,role,joined_at,user_profiles!team_members_user_id_fkey(id,email,first_name,last_name,user_roles!user_roles_user_id_fkey(role_id,roles(id,name)))&id=eq.${createdMember.id}`
      );

      if (!memberResponse.ok) {
        console.warn(
          "Warning: Could not fetch complete member data, returning partial data"
        );
        return createdMember;
      }

      const memberData = await memberResponse.json();
      return memberData && memberData.length > 0
        ? memberData[0]
        : createdMember;
    } catch (error) {
      console.error("Error in addTeamMember:", error);
      throw error;
    }
  }

  /**
   * Remove a user from a team
   */
  static async removeTeamMember(
    teamMemberId: string,
    tenantId: string
  ): Promise<void> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot remove team member");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/team_members?id=eq.${teamMemberId}&tenant_id=eq.${tenantId}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error removing team member:", error);
        throw new Error(`Failed to remove team member: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error in removeTeamMember:", error);
      throw error;
    }
  }
}
